import { DialogConfig } from '@agent-ds/shared/components/dialog/dialog-config';
import { DialogRef } from '@agent-ds/shared/components/dialog/dialog-ref';
import { StudentAIInterviewType } from '@agent-ds/shared/enums';
import { StudentDetail, StudentAIInterviewRequest } from '@agent-ds/shared/interfaces';
import { FormMeta, RowMeta } from '@agent-ds/shared/models';
import { DynamicFieldService, StudentApiService } from '@agent-ds/shared/services';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ag-student-gps-academic-create-dialog',
  templateUrl: './student-gps-academic-create-dialog.component.html',
  styleUrls: ['./student-gps-academic-create-dialog.component.scss'],
})
export class StudentGPSAcademicCreateDialogComponent implements OnInit, OnDestroy {
  constructor(
    private dynamicService: DynamicFieldService,
    private studentApiService: StudentApiService,
    public dialog: DialogRef,
    public config: DialogConfig<{ type: StudentAIInterviewType; student: StudentDetail }>,
  ) {}
  type: StudentAIInterviewType;
  student: StudentDetail;
  saving = false;
  pageTitle: string;

  // studentGPSAcademicInfoData: any = {};
  studentGPSAcademicDataModel: Record<string, any> = {};

  studentGPSAcademicMeta: FormMeta;
  private fieldSubscription: Subscription;

  ngOnInit() {
    this.type = this.config.data.type;
    this.student = this.config.data.student;
    this.pageTitle = this.type === StudentAIInterviewType.GPS ? 'GPS' : 'AI面談・AI面接対策';
    this.fieldSubscription = this.dynamicService.fieldUpdateEvent.subscribe(() => {
      this.initFieldsMeta();
    });
  }

  ngOnDestroy() {
    if (this.fieldSubscription) {
      this.fieldSubscription.unsubscribe();
      this.fieldSubscription = null;
    }
  }

  private initFieldsMeta() {
    const rows: RowMeta[] = this.type === StudentAIInterviewType.GPS ? this.getGPSRows() : this.getPeopleXRows();
    const title: string = this.type === StudentAIInterviewType.GPS ? '新規GPS情報' : '新規PeopleX情報';

    this.studentGPSAcademicMeta = {
      disabled: false,
      initTouched: true,
      groups: [
        {
          title: title,
          class: 'form__group--green-title-border',
          rows: rows,
        },
      ],
    };
  }

  private getGPSRows(): RowMeta[] {
    const dateDefinition = {
      ...this.dynamicService.getDefinition('student_ai_interview', 'gpsExaminationDate'),
      validationStyle: { required: true }
    };
    const idDefinition = {
      ...this.dynamicService.getDefinition('student_ai_interview', 'gpsCandidateId'),
      validationStyle: { required: true }
    };

    return [
      ...this.dynamicService.getFormRows(dateDefinition),
      ...this.dynamicService.getFormRows(idDefinition, null, 'full'),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityOverviewRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityOverviewScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityCriticalThinkingSkillsRank'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityCriticalThinkingSkillsScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityCollaborativeThinkingRank'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityCollaborativeThinkingScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityCreativeThinkingAbilityRank'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityCreativeThinkingAbilityScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeResilienceRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeResilienceScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeEmotionalControlRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeEmotionalControlScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeQuickRecoveryRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeQuickRecoveryScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeRespondsCalmlyRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeRespondsCalmlyScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeLeadershipRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeLeadershipScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeTakeLeadRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeTakeLeadScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeChallengeTheUnknownRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeChallengeTheUnknownScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudePersistenceRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudePersistenceScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeCollaborationRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeCollaborationScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeUnderstandingOfOthersRank')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeUnderstandingOfOthersScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeProactivenessForInteractingWithOthersRank'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeProactivenessForInteractingWithOthersScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceOverviewRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceOverviewScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceSelfManagementRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceSelfManagementScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceChallengingRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceChallengingScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceContinueRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceContinueScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceDealingWithStressRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceDealingWithStressScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceInterpersonalRelationshipsRank'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceInterpersonalRelationshipsScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceEmbracingDiversityRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceEmbracingDiversityScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceBuildRelationshipsRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceBuildRelationshipsScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceDiscussRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceDiscussScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsExperiencePlanningAndExecutionRank'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsExperiencePlanningAndExecutionScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceSetupAssignmentRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceSetupAssignmentScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceDevelopSolutionRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceDevelopSolutionScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceExecuteAndVerifyRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceExecuteAndVerifyScore')),
    ];
  }

  private getPeopleXRows(): RowMeta[] {
    const dateDefinition = {
      ...this.dynamicService.getDefinition('student_ai_interview', 'peoplexAIInterviewDate'),
      validationStyle: { required: true }
    };
    const idDefinition = {
      ...this.dynamicService.getDefinition('student_ai_interview', 'peoplexAIInterviewName'),
      validationStyle: { required: true }
    };

    return [
      ...this.dynamicService.getFormRows(dateDefinition),
      ...this.dynamicService.getFormRows(idDefinition, null, 'full'),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexComprehensiveEvaluationOverviewRank'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexComprehensiveEvaluationOverviewScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexComprehensiveEvaluationOverview'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexComprehensiveEvaluationStrengths'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexComprehensiveEvaluationImprovementPoints'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexLanguageOverviewScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexLanguageExpressivePowerScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexLanguageUnderstandingAbilityScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexLanguageLogicalThinkingAbilityScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexNonverbalOverviewScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexNonVerbalActionsExpressionsScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexNonVerbalLanguageMannersScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexNonVerbalToneSpeedScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexAttitudeValuesOverviewScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexAttitudeValuesWillingnessGrowScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexAttitudeValuesCareerVisionScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexAttitudeValuesSenseResponsibilityScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexKnowledgeSkillOverviewScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexKnowledgeSkillProblemFindingAndSolvingSkillsScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexKnowledgeSkillTeamworkScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexKnowledgeSkillEntrainmentAndPropulsionForceScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexKnowledgeSkillManagementExperienceScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexBehavioralCharacteristicsOverviewScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexBehavioralCharacteristicsIndependenceAspirationsScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexBehavioralCharacteristicsStressToleranceScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexBehavioralCharacteristicsEffortPersistenceScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexDegreeOfInterestUnderstandingOfCompanyScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexConsistencyMotivationForApplyingAndReasonsForLeavingScore'),
      ),
    ];
  }

  public isValidStudentGPSAcademicInfoData(): boolean {
    if (this.type === StudentAIInterviewType.GPS) {
      return !!this.studentGPSAcademicDataModel.gpsExaminationDate
        && !!this.studentGPSAcademicDataModel.gpsCandidateId;
    } else {
      return !!this.studentGPSAcademicDataModel.peoplexAIInterviewDate
        && !!this.studentGPSAcademicDataModel.peoplexAIInterviewName;
    }
  }

  public close(): void {
    this.dialog.close();
  }

  public reflect(): void {
    if (!this.isValidStudentGPSAcademicInfoData()) {
      // 必須の date & id が未選択
      return;
    }

    if (this.saving) {
      return;
    }
    this.saving = true;

    const payload: StudentAIInterviewRequest = {
      type: this.type,
      dynamicData: this.studentGPSAcademicDataModel
    };

    this.studentApiService.createStudentAIInterviewEntry(this.student.id, payload).subscribe(
      () => {
        this.saving = false;
        // 親側で拾えるように返す（必要なら）
        this.dialog.close({ saved: true });
      },
      () => {
        this.saving = false;
      },
    );
  }
}
